import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

const VIEWPORT = { once: true, margin: "-80px" };

// One consistent scroll-in motion for the whole site.
const Reveal = ({ as = "div", delay = 0, y = 24, className = "", children, ...rest }) => {
  const Tag = motion[as] || motion.div;
  return (
    <Tag
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={VIEWPORT}
      transition={{ duration: 0.7, delay, ease: EASE }}
      className={className}
      {...rest}
    >
      {children}
    </Tag>
  );
};

const groupVariants = {
  hidden: {},
  show: (stagger = 0.08) => ({ transition: { staggerChildren: stagger } }),
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

// Driven by an explicit animate state (not whileInView) so items that mount later, e.g. after a
// language switch swaps the list, still animate to "show" instead of staying hidden.
export const RevealGroup = ({ as = "div", stagger = 0.08, className = "", children, ...rest }) => {
  const Tag = motion[as] || motion.div;
  const ref = useRef(null);
  const inView = useInView(ref, VIEWPORT);
  return (
    <Tag
      ref={ref}
      variants={groupVariants}
      custom={stagger}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      className={className}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export const RevealItem = ({ as = "div", className = "", children, ...rest }) => {
  const Tag = motion[as] || motion.div;
  return (
    <Tag variants={itemVariants} className={className} {...rest}>
      {children}
    </Tag>
  );
};

// Splits a title into a light lead-in and a heavy ending, e.g. "The Work | Behind the Work."
export const splitTitle = (text = "") => {
  const sentence = text.match(/^(.+?[.?!؟])\s+(.+)$/);
  if (sentence) return [sentence[1], sentence[2]];
  const words = text.split(" ");
  if (words.length < 2) return ["", text];
  const cut = Math.max(1, Math.floor(words.length * 0.4));
  return [words.slice(0, cut).join(" "), words.slice(cut).join(" ")];
};

export const SplitTitle = ({ text, as: Tag = "h2", className = "", heavyClassName = "" }) => {
  const [light, heavy] = splitTitle(text);
  return (
    <Tag className={className}>
      {light && <span className="font-light">{light} </span>}
      <span className={`font-extrabold ${heavyClassName}`}>{heavy}</span>
    </Tag>
  );
};

export default Reveal;
